import { Anchor, Badge, Box, Button, Group, Modal, Paper, NumberInput, Skeleton, Stack, Table, Text, TextInput, UnstyledButton, VisuallyHidden, type MantineColor } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import type { OrgCount } from '@motionsafe/ms-console-api/admin/v1/org_pb.js';
import { ConsoleRole, OrgStatus, Product } from '@motionsafe/ms-console-api/common/v1/common_pb.js';
import { IconBuildingCommunity, IconPlus, IconSearch } from '@tabler/icons-react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { useId, useState } from 'react';

import { api } from '../../api/client';
import { allUsersQuery, errorText, meQuery, orgCountsQuery, useInvalidate, useOrgNames } from '../../api/hooks';
import { useGridRows } from '../../components/grid';
import { OrgActions } from '../../components/license';
import { locked } from '../../components/locked';
import { useServerPages } from '../../components/pager';
import { orgsIn } from '../../lib/counts';
import { EmptyState, PageHeader, ToolbarActions, type StatProps } from '../../components/page';
import { fraction, poolText } from '../../lib/format';
import { isStaff } from '../../lib/roles';
import { ICON_INLINE } from '../../theme';
import { useAccess } from '../_app';
import { DialogLoadBar } from '../../components/top-load-bar';

// Staff of either kind see every organization here; read-only staff see
// every control locked.
export const Route = createFileRoute('/_app/orgs')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.fetchQuery(meQuery);
    if (!me || !isStaff(me)) throw redirect({ to: '/overview', search: {} });
  },
  component: Orgs,
});

const statusColor = (s: OrgStatus): MantineColor => (s === OrgStatus.ACTIVE ? 'success' : s === OrgStatus.UNSPECIFIED ? 'gray' : 'danger');
const statusWord = (s: OrgStatus) => OrgStatus[s].toLowerCase().replace(/_/g, ' ');

// Products are named as the product is sold.
const PRODUCT_NAME: Partial<Record<Product, string>> = { [Product.INSIGHTS]: 'Insights', [Product.PRIVACY]: 'Privacy' };
const productNames = (ps: Product[]) => ps.map((p) => PRODUCT_NAME[p] ?? Product[p].toLowerCase()).join(', ');

const COLUMNS = ['Name', 'Slug', 'Status', 'Seats', 'Devices', 'Products'] as const;
// Every column but the name has its own width, so nothing shifts from one page to the next.
const COLUMN_WIDTH: Record<string, number> = { Slug: 210, Status: 130, Seats: 180, Devices: 100, Products: 170 };
const SKELETON_WIDTH = ['70%', '60%', 64, '55%', '50%', '65%', '60%'] as const;

function None() {
  return <Text span c="dimmed" inherit>none</Text>;
}

// Every customer by status, regardless of the search.
function statusStats(groups: OrgCount[]): StatProps[] {
  const count = (s: OrgStatus) => orgsIn(groups, (g) => g.status === s);
  const closed = count(OrgStatus.CLOSED);
  const suspended = count(OrgStatus.SUSPENDED);
  return [
    { label: 'Organizations', value: orgsIn(groups), hint: closed > 0 ? `${closed} closed` : 'None closed' },
    { label: 'Active', value: count(OrgStatus.ACTIVE), hint: 'People can sign in' },
    { label: 'Suspended', value: suspended, hint: suspended > 0 ? 'No one can sign in' : 'None suspended', tone: suspended > 0 ? 'bad' : 'neutral' },
  ];
}

function Orgs() {
  // A new organization is Accounts' to create.
  const access = useAccess();
  const write = access.may('CreateOrg');
  const newOrgCaption = useId();
  const [q, setQ] = useState('');
  const [debounced] = useDebouncedValue(q, 200);
  // Clearing applies at once; typing waits for a pause.
  const term = q.trim() ? debounced.trim() : '';
  const counts = useQuery(orgCountsQuery()).data;
  const list = useServerPages({ key: ['orgs', 'list'], narrowed: term, read: (page) => api.org.listOrgs({ query: term, ...page }), noun: 'organizations' });
  const orgs = list.query;
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  // A row opens its organization; what can be changed about it is on the row's menu.
  const open = (id: string) => void navigate({ to: '/overview', search: { org: id } });
  const rows = orgs.data?.orgs ?? [];
  const gridRow = useGridRows(rows.map((o) => o.id));
  const showTable = orgs.isPending || rows.length > 0;
  const total = counts ? orgsIn(counts) : undefined;
  const newOrg = write
    ? <Button size="sm" leftSection={<IconPlus {...ICON_INLINE} />} onClick={() => setCreating(true)}>New organization</Button>
    : <Button size="sm" variant="default" leftSection={<IconPlus {...ICON_INLINE} />} {...locked(newOrgCaption)}>New organization</Button>;

  return (
    <>
      <PageHeader
        title="All organizations"
        description="Every customer on the console. Open one to work inside it as MotionSafe: its people, devices, keys, policy, and audit trail."
        stats={counts ? statusStats(counts) : 'loading'}
        statsCount={3}
      />

      <Group gap="sm" align="center" wrap="wrap" mb="md">
        <TextInput placeholder="Search organizations or people" value={q} onChange={(e) => setQ(e.currentTarget.value)} aria-label="Search organizations, or people by name or email" leftSection={<IconSearch {...ICON_INLINE} />} w={{ base: '100%', xs: 320 }} />
        <ToolbarActions note={write ? undefined : access.caption('CreateOrg')} noteId={newOrgCaption}>
          {orgs.isSuccess && total !== undefined && (
            <Text size="sm" c="dimmed" className="num">
              {term ? `${fraction(list.total, total)} match` : total === 1 ? '1 organization' : `${total} organizations`}
            </Text>
          )}
          {newOrg}
        </ToolbarActions>
      </Group>

      {term.length >= 2 && <PeopleFound term={term} />}

      <Paper p={0} style={{ overflow: 'hidden' }}>
        {showTable && (
          <Table.ScrollContainer minWidth={960}>
            <Table role="grid" layout="fixed" aria-label="Organizations" aria-busy={orgs.isPending || orgs.isPlaceholderData} style={{ opacity: orgs.isPlaceholderData ? 0.6 : 1, transition: 'opacity var(--motion-micro, 120ms) ease-out' }}>
              <Table.Thead>
                <Table.Tr>{COLUMNS.map((c) => <Table.Th key={c} {...(COLUMN_WIDTH[c] ? { w: COLUMN_WIDTH[c] } : {})}>{c}</Table.Th>)}<Table.Th w={52}><VisuallyHidden>Actions</VisuallyHidden></Table.Th></Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {orgs.isPending && [0, 1, 2, 3, 4, 5].map((i) => (
                  <Table.Tr key={`loading-${i}`} aria-hidden="true" bg="transparent">
                    {COLUMNS.map((c, j) => <Table.Td key={c}><Skeleton height={14} width={SKELETON_WIDTH[j] ?? '60%'} my={5} /></Table.Td>)}<Table.Td />
                  </Table.Tr>
                ))}
                {rows.map((o) => {
                  return (
                    <Table.Tr
                      key={o.id}
                      {...gridRow(o.id, () => open(o.id))}
                      onClick={() => open(o.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Table.Td fw={500}>{o.name}</Table.Td>
                      <Table.Td><span className="id">{o.slug}</span></Table.Td>
                      <Table.Td><Badge color={statusColor(o.status)}>{statusWord(o.status)}</Badge></Table.Td>
                      <Table.Td className="num" style={{ whiteSpace: 'nowrap' }}>
                        {poolText(o.seatPool)}
                        {o.seatPool?.unlimited ? <Text span c="dimmed" inherit>, no pool</Text> : o.seatPool?.pending ? <Text span c="dimmed" inherit>, {o.seatPool.pending} pending</Text> : null}
                      </Table.Td>
                      <Table.Td className="num" style={{ whiteSpace: 'nowrap' }}>{o.devicesOnRecord}</Table.Td>
                      <Table.Td>{productNames(o.products) || <None />}</Table.Td>
                      {/* The menu is the row's own: a click or a key in it does not open the organization. */}
                      <Table.Td ta="right" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                        <OrgActions org={o} compact withOpen />
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
        {list.footer}
        {orgs.isError && <Text size="sm" c="danger" ta="center" py="lg" px="lg">{errorText(orgs.error)}</Text>}
        {orgs.isSuccess && rows.length === 0 && (
          term
            ? <EmptyState icon={IconSearch} title="No organizations match" body={`Nothing matches “${term}” in a name or slug.`} />
            : <EmptyState icon={IconBuildingCommunity} title="No organizations yet" body="An organization is created active, with its seat pool and first key." />
        )}
      </Paper>

      {write && <CreateOrgDialog open={creating} onClose={() => setCreating(false)} />}
    </>
  );
}

// The people a search finds, in any organization: who they are, where, and
// the way to them. It answers "which organization is this person in?"
// without a second list of everyone.
function PeopleFound({ term }: { term: string }) {
  const found = useQuery({ ...allUsersQuery(0, term), placeholderData: keepPreviousData });
  const people = (found.data ?? []).slice(0, 8);
  const named = useOrgNames(people.map((u) => u.orgId));
  if (people.length === 0) return null;
  const orgName = (id: string) => named(id) || 'unknown organization';
  return (
    <Paper p={0} mb="md" style={{ overflow: 'hidden' }} component="section" aria-label="People found">
      <Text size="xs" c="dimmed" fw={600} px="lg" pt="md" pb={6}>People</Text>
      {people.map((u) => {
        const operator = u.consoleRole === ConsoleRole.OPERATOR;
        return (
          <UnstyledButton
            key={u.id}
            className="found-row"
            renderRoot={(p) => <Link {...p} to="/people" search={{ org: u.orgId }} />}
          >
            <Box miw={0} flex={1}>
              <Text size="sm" fw={500} truncate>{u.displayName}</Text>
              <Text size="xs" c="dimmed" truncate>{u.email}</Text>
            </Box>
            <Text size="sm" c="dimmed" ta="right" style={{ flex: 'none' }}>{operator ? 'Operator' : 'Administrator'} at {orgName(u.orgId)}</Text>
          </UnstyledButton>
        );
      })}
      {(found.data?.length ?? 0) > people.length && <Text size="xs" c="dimmed" px="lg" py="sm">More match; type more of the name or email.</Text>}
    </Paper>
  );
}

// What is typed, made into a slug: lowercase, spaces as hyphens, nothing else kept.
function toSlug(text: string): string {
  return text.toLowerCase().replace(/[\s_]+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-{2,}/g, '-').slice(0, 32);
}

function CreateOrgDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  // Linking to one at the identity provider is an admin's, so only an admin is offered it.
  const mayLink = useAccess().unrestricted;
  const inv = useInvalidate();
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [seats, setSeats] = useState<string | number>(2);
  const [idpOrg, setIdpOrg] = useState('');
  const [linking, setLinking] = useState(false);
  // The slug follows the name until the person types one of their own.
  const [slugTyped, setSlugTyped] = useState(false);
  const [error, setError] = useState('');
  // A refusal is shown under the field it is about.
  const about = error.includes('slug') ? 'slug' : error.includes('identity provider') ? 'idp' : 'form';
  const clear = () => { setSlug(''); setName(''); setSeats(2); setIdpOrg(''); setLinking(false); setSlugTyped(false); setError(''); };
  const create = useMutation({
    mutationFn: () => api.org.createOrg({ slug, name, seatCount: Number(seats), idpOrgId: idpOrg.trim(), products: [Product.INSIGHTS, Product.PRIVACY] }),
    onSuccess: (r) => {
      void inv('orgs');
      // The organization stands either way; a key that was not made is said, with where to make it.
      if (r.firstKey && r.keyRefusal) notifications.show({ color: 'warning', autoClose: false, message: `${name} created, with ${r.firstKey} as its key, and one thing went wrong: ${r.keyRefusal}. People can sign in on devices. Tell MotionSafe engineering, so the key's record in the vault is written.` });
      else if (r.firstKey) notifications.show({ message: `${name} created, with ${r.firstKey} as its key.${r.peopleAdded ? ` ${r.peopleAdded} ${r.peopleAdded === 1 ? 'person' : 'people'} from the identity provider ${r.peopleAdded === 1 ? 'is' : 'are'} waiting for a seat.` : ''}` });
      else notifications.show({ color: 'warning', autoClose: false, message: `${name} created, without a key: ${r.keyRefusal || 'the key could not be generated'}. Nobody can sign in on a device until it has one. Generate it on the organization's Keys page.` });
      onClose(); clear();
    },
    onError: (e) => setError(errorText(e)),
  });
  const ready = !create.isPending && !!slug && !!name && typeof seats === 'number';
  // Each opening starts empty, not with what was typed before Cancel. Not
  // while the organization is being made, so its answer lands here.
  const close = () => { if (create.isPending) return; clear(); onClose(); };
  return (
    <Modal opened={open} onClose={close} title="New organization" size={440}>
      <DialogLoadBar />
      <form onSubmit={(e) => { e.preventDefault(); if (ready) create.mutate(); }}>
        <Stack gap="lg">
          {/* The two things every organization is asked for, on one line; neither needs explaining. */}
          <Group gap="md" align="flex-start" wrap="nowrap">
            <TextInput label="Name" withAsterisk flex={1} miw={0} value={name} onChange={(e) => { const v = e.currentTarget.value; setName(v); if (!slugTyped) setSlug(toSlug(v).replace(/^-+|-+$/g, '')); }} data-autofocus />
            <NumberInput label="Seats" withAsterisk w={112} value={seats} onChange={setSeats} min={1} max={100000} allowDecimal={false} allowNegative={false} classNames={{ input: 'num' }} />
          </Group>
          <TextInput
            label="Slug"
            withAsterisk
            description="The organization's ID inside every key. It cannot change."
            error={about === 'slug' ? error : undefined}
            value={slug}
            onChange={(e) => { setSlugTyped(true); setSlug(toSlug(e.currentTarget.value)); if (about === 'slug') setError(''); }}
            autoComplete="off"
            spellCheck={false}
            styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
          />
          {/* Rare, so it is asked for only by someone who came to do it. */}
          {!mayLink ? null : linking ? (
            <TextInput
              label="Identity provider organization"
              description="The ID of one that exists there already. It begins org_."
              value={idpOrg}
              onChange={(e) => { setIdpOrg(e.currentTarget.value.trim()); if (about === 'idp') setError(''); }}
              error={about === 'idp' ? error : undefined}
              autoComplete="off"
              spellCheck={false}
              autoFocus
              styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
            />
          ) : (
            <Anchor component="button" type="button" size="sm" ta="left" onClick={() => setLinking(true)}>Link one that exists at the identity provider</Anchor>
          )}
          {about === 'form' && error && <Text size="sm" c="danger" role="alert">{error}</Text>}
          <Group className="dialog-footer" justify="flex-end" gap="sm">
            <Button variant="default" onClick={close} disabled={create.isPending}>Cancel</Button>
            <Button type="submit" disabled={!ready || create.isPending}>Create organization</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
